import {useEffect, useState} from 'react';
import {z} from 'zod';

import {trackRunOverviewSdkVersionFetchError} from '../services/analytics/productEvents';

// only fields we need and read from pypi response
const PypiResponseSchema = z.object({
  info: z.object({version: z.string()}),
  releases: z.record(
    z.string(),
    z.array(z.object({upload_time_iso_8601: z.string().optional()}))
  ),
});

export interface WandbSdkVersionInfo {
  latestVersion: string;
  releasesByVersion: Record<string, string>; // [version, ISO8601 upload time]
}
export function parsePypiResponse(rawJson: unknown): WandbSdkVersionInfo {
  // throws if at minimum zod schema is not satisfied -> means api changed
  const parsed = PypiResponseSchema.parse(rawJson);

  const releasesByVersion: Record<string, string> = {};
  for (const [version, artifacts] of Object.entries(parsed.releases)) {
    let latestUploadTime: string | undefined;
    for (const artifact of artifacts) {
      const uploadTime = artifact.upload_time_iso_8601;
      if (uploadTime == null) {
        continue;
      }
      if (latestUploadTime == null || uploadTime > latestUploadTime) {
        latestUploadTime = uploadTime;
      }
    }
    if (latestUploadTime != null) {
      releasesByVersion[version] = latestUploadTime;
    }
  }

  return {latestVersion: parsed.info.version, releasesByVersion};
}

// singleton promise, cache per browser session
let cachedPromise: Promise<WandbSdkVersionInfo> | null = null;
function fetchLatestWandbSdkVersion(): Promise<WandbSdkVersionInfo> {
  if (cachedPromise != null) {
    return cachedPromise;
  }
  cachedPromise = fetch('https://pypi.org/pypi/wandb/json')
    .then(res => {
      if (!res.ok) {
        throw new Error(`pypi fetch failed: ${res.status}`);
      }
      return res.json();
    })
    .then(parsePypiResponse)
    .catch(err => {
      cachedPromise = null;
      throw err;
    });
  return cachedPromise;
}

// exposed for tests only
export function _resetSdkVersionCacheForTesting(): void {
  cachedPromise = null;
}

/**
 * @returns the latest wandb sdk version and the releases by version
 */
export function useLatestWandbSdkVersion(): {
  data: WandbSdkVersionInfo | null;
  loading: boolean;
  error: Error | null;
} {
  const [data, setData] = useState<WandbSdkVersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchLatestWandbSdkVersion()
      .then(result => {
        setData(result);
        setLoading(false);
      })
      .catch(err => {
        setError(new Error('Failed to fetch wandb sdk version'));
        setLoading(false);
        trackRunOverviewSdkVersionFetchError({error: err.message});
      });
  }, []);

  return {data, loading, error};
}
